import NOTIFICATION_TYPES from "@common/constants/notificationTypes";
import COMMAND_TYPES from "@common/constants/commandTypes";
import * as mqttTopics from "@common/constants/mqttTopics";
import { of, timer } from "rxjs";
import { mapTo, mergeMap, retryWhen, scan, tap, timeout } from "rxjs/operators";

const FREQUENT_LOG_RETRY_BASE_MS = 5000;
const FREQUENT_LOG_RETRY_MAX_MS = 5 * 60 * 1000;

/**
 * Simple collections of functions to make it more clear what we are exactly checking in
 * notifications or logs
 */
const mqttUtil = {
  hasNoNewNextUseraction: frequentlog =>
    frequentlog.logsList[0].nextUserAction === undefined,
  hasNoNewPreviousUseraction: frequentlog =>
    frequentlog.logsList[0].previousUserAction === undefined,
  hasNewUseraction: (frequentlog, prevUseractionId) => {
    return (
      frequentlog.logsList[0].currentStatus.action !== undefined &&
      frequentlog.logsList[0].currentStatus.action !== prevUseractionId
    );
  },
  hasNoUseraction: frequentlog => {
    return frequentlog.logsList[0].currentStatus.action === undefined;
  },
  hasNewCurrentTemp: (frequentlog, oldTemp) => {
    return frequentlog.logsList[0].currentTemp !== oldTemp;
  },
  hasNewTargetTemp: (frequentlog, oldTemp) => {
    return frequentlog.logsList[0].setpointTemp !== oldTemp;
  },
  processSummaryChanged: notification => {
    return (
      notification.type === NOTIFICATION_TYPES.NOT_PROCESS_ESTIMATION_CHANGED
    );
  },
  sessionStateChanged: notification => {
    return notification.type === NOTIFICATION_TYPES.NOT_SESSION_STATE_CHANGED;
  },
  hasError: notification => {
    return notification.error !== 0 && notification.error !== undefined;
  },
  isExecuted: notification => {
    return notification.type === NOTIFICATION_TYPES.NOT_COMMAND_EXECUTED;
  },
  sessionIsStopped: notification => {
    return (
      notification.type === NOTIFICATION_TYPES.NOT_COMMAND_EXECUTED &&
      notification.command === COMMAND_TYPES.CMD_STOP_PROCESS
    );
  },
  getAllSubscriptionsByType: (type, subscriptions) => {
    return subscriptions.filter(s => s.startsWith(type));
  },
  findNoNUsedFrequentLogSubscriptions: function(uuid, subscriptions) {
    const filtered = this.getAllSubscriptionsByType(
      mqttTopics.logsTopic,
      subscriptions
    );
    return filtered.filter(s => !s.includes(uuid));
  }
};
/**
 * Delay before the n-th consecutive frequent-log retry. The first retry is
 * immediate (a single missed frame is normal); after that it doubles from 5 s
 * up to 5 min so a device that never answers doesn't get hammered forever.
 */
mqttUtil.frequentLogRetryDelayMs = (attempt) => {
  if (attempt <= 1) return 0;
  return Math.min(
    FREQUENT_LOG_RETRY_BASE_MS * 2 ** (attempt - 2),
    FREQUENT_LOG_RETRY_MAX_MS,
  );
};

/**
 * Wraps a frequent-log stream with a timeout + exponential-backoff retry.
 * `onRetry(attempt)` is called before each retry so the caller can re-request
 * logs; the attempt counter resets every time a log actually arrives.
 */
mqttUtil.withFrequentLogRetry = (source$, { onRetry, timeoutMs = 5000 }) => {
  let attempt = 0;
  return source$.pipe(
    timeout(timeoutMs),
    tap(() => {
      attempt = 0;
    }),
    retryWhen((errors$) =>
      errors$.pipe(
        scan(() => {
          attempt += 1;
          return attempt;
        }, 0),
        mergeMap((n) => {
          const delay = mqttUtil.frequentLogRetryDelayMs(n);
          return delay > 0 ? timer(delay).pipe(mapTo(n)) : of(n);
        }),
        tap((n) => onRetry(n)),
      ),
    ),
  );
};

export default mqttUtil;
